import './App.css';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { initGA } from './analytics';
import Breadcrumb from './components/Breadcrumb';
import { useAnalytics } from './hooks/useAnalytics';
import { initPerformanceTracking, initVisibilityTracking, trackConnectionQuality, trackDeviceCapabilities } from './utils/performance';

import FooterSection from './modules/footer/footer';
import ScheduleSection from './modules/schedule/schedule';
import DownloadPage from './pages/DownloadPage';
import FlowryPage from './pages/FlowryPage';
import FinOpsPage from './pages/FinOpsPage';

function FlowryAlias() {
  const { hash, search } = useLocation();
  return <Navigate to={{ pathname: '/', hash, search }} replace />;
}

function App() {
  const { t, i18n } = useTranslation();
  const { trackEvent, trackPageView, trackLanguageSwitch, trackTimeOnPage } = useAnalytics();

  useEffect(() => {
    initGA();  
    trackPageView(window.location.pathname);

    // Initialize performance tracking
    initPerformanceTracking((metric, value) => {
      trackEvent({
        category: 'Performance',
        action: 'Web Vital',
        label: metric,
        value: Math.round(value)
      });
    });

    // Initialize visibility tracking
    const cleanupVisibility = initVisibilityTracking(trackEvent);

    // Track device capabilities
    trackDeviceCapabilities(trackEvent);
    trackConnectionQuality(trackEvent);

    // Track time on page
    const startTime = Date.now();
    const trackTimeOnPageInterval = setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      if (timeSpent > 0 && timeSpent % 30 === 0) { // Track every 30 seconds
        trackTimeOnPage(window.location.pathname, timeSpent);
      }
    }, 1000);

    return () => {
      cleanupVisibility();
      clearInterval(trackTimeOnPageInterval);
    };
  }, [trackEvent, trackPageView, trackTimeOnPage]);

  const toggleLanguage = () => {
    const newLanguage = i18n.language === 'en' ? 'fr' : 'en';
    trackLanguageSwitch(i18n.language, newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  return (
    <HelmetProvider>
      <Router>
        <div className="App">
        <header className="App-header">
          <Link to="/" className="logo">
            <img src="/CloudSibyl-logo.png" alt="CloudSibyl Logo" />
          </Link>
          <nav className="header-nav" aria-label="Page">
            <Link className="nav-link" to="/">{t('nav_flowry')}</Link>
            <Link className="nav-link" to="/finops">{t('nav_finops')}</Link>
          </nav>
          <div className="header-buttons">
            <button className="lang-button" onClick={toggleLanguage}>
              {i18n.language === 'en' ? 'FR' : 'EN'}
            </button>
            <ScheduleSection />
          </div>
        </header>
        
        <Breadcrumb />

        <Routes>
          <Route path="/" element={<FlowryPage />} />
          <Route path="/flowry" element={<FlowryAlias />} />
          <Route path="/finops" element={<FinOpsPage />} />
          <Route path="/cloud-blind-spots" element={<DownloadPage />} />
        </Routes>
        <FooterSection />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
